/**
 * A utility function that allows us to mark certain fields on objects returned
 * from accessors as deprecated.
 *
 * NOTE: (Stefan) It is **important** to note that the function assumes that
 * the accessor and it's data is ready, as it memoizes the result internally.
 * It is undefined behaviour if you call this on an accessor whose data is not
 * ready at the time of public API initialization.
 */
export const decorateAccessorWithDeprecationNotice = <T>(params: {
    accessor: () => T | undefined | null;
    deprecatedKeys: Array<keyof T>;
    deprecationMessage: string;
    logger: (msg: string) => void;
}) => {
    const rValue = params.accessor();
    if (rValue === undefined || rValue === null || typeof rValue !== 'object') {
        return params.accessor as () => T;
    }

    params.deprecatedKeys.forEach(key => {
        // (Maxim) read it first, then wrap with logger
        // Accessing same prop from inside of the accessor causes infinite loop
        const deprecatedPropValue = rValue[key];
        Object.defineProperty(rValue, key, {
            get: () => deprecatedPropValue,
        });
    });

    return () => rValue;
};
